/**
 * AppConfirmModal — modal de confirmación reusable estilo Polaris.
 *
 * Reemplaza:
 *   - El patrón duplicado de `<Modal>` + `primaryAction: { destructive: true }`
 *     que se repite en 8+ páginas (Roles, Reglas, Equipos, Atributos, Macros,
 *     Etiquetas, Entradas, Contactos, Respuestas rápidas, Contactos).
 *   - Los `window.confirm()` nativos feos esparcidos en Skills, Campaigns,
 *     Inbox, ConnectionSection, IdentitySection.
 *   - El helper `waConfirmDelete()` que también usaba `window.confirm()`.
 *
 * Uso (caso típico de eliminación):
 *
 *   const [deleteTarget, setDeleteTarget] = useState<MyItem | null>(null);
 *
 *   ...
 *   <AppConfirmModal
 *     open={!!deleteTarget}
 *     onClose={() => setDeleteTarget(null)}
 *     title="Eliminar X"
 *     message={deleteTarget ? `¿Estás seguro de eliminar "${deleteTarget.name}"?` : ''}
 *     description="Esta acción no se puede deshacer."
 *     confirmLabel={deleteTarget ? `Sí, eliminar ${deleteTarget.name}` : 'Eliminar'}
 *     destructive
 *     onConfirm={async () => {
 *       await supabase.from('table').delete().eq('id', deleteTarget!.id);
 *       setDeleteTarget(null);
 *       void load();
 *     }}
 *   />
 *
 * Para casos imperativos donde `await confirm(...)` es más ergonómico,
 * ver el hook `useConfirmDialog()` (a crear en una iteración futura).
 */

import { useCallback, useState, type ReactNode } from 'react';
import { Modal, Text, Box } from '@/lib/polaris';

export interface AppConfirmModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Mensaje principal — soporta string o ReactNode para cuando necesitas
   *  resaltar un nombre con `<strong>`. */
  message: ReactNode;
  /** Texto secundario (subdued) — típicamente "Esta acción no se puede deshacer". */
  description?: ReactNode;
  /** Label del botón primary. Default: "Confirmar". */
  confirmLabel?: string;
  /** Label del botón secundario. Default: "Cancelar". */
  cancelLabel?: string;
  /** Si true, el botón primary se renderiza en rojo (destructive). Default: false. */
  destructive?: boolean;
  /**
   * Bloquea el botón de confirmar. Existe para las confirmaciones que exigen un paso previo —
   * escribir el nombre de lo que se borra, por ejemplo. Sin esto, la única forma de agregar
   * fricción era abandonar este modal y escribir uno propio, que es cómo se llega a ocho modales
   * de confirmación distintos.
   */
  confirmDisabled?: boolean;
  /** Handler async — el modal muestra spinner en el botón mientras corre. */
  onConfirm: () => void | Promise<void>;
}

export function AppConfirmModal({
  open,
  onClose,
  title,
  message,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  confirmDisabled = false,
  onConfirm,
}: AppConfirmModalProps) {
  const [busy, setBusy] = useState(false);

  const handleConfirm = useCallback(async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  }, [onConfirm]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      primaryAction={{
        content: confirmLabel,
        destructive,
        loading: busy,
        disabled: confirmDisabled,
        onAction: () => void handleConfirm(),
      }}
      secondaryActions={[{ content: cancelLabel, onAction: onClose }]}
    >
      <Modal.Section>
        {typeof message === 'string' ? (
          <Text as="p" variant="bodyMd">{message}</Text>
        ) : (
          message
        )}
        {description && (
          <Box paddingBlockStart="200">
            {typeof description === 'string' ? (
              <Text as="p" variant="bodySm" tone="subdued">{description}</Text>
            ) : (
              description
            )}
          </Box>
        )}
      </Modal.Section>
    </Modal>
  );
}
